import React, { useState, useEffect } from 'react';
import { generateDatabaseSchema, exportSchemaToJSON, exportSchemaAsHTML } from '../../../../utils/database/schemaGenerator';
import { Button } from '@/components/common/Button';
import { Database, Download, RefreshCw, AlertTriangle, Info } from 'lucide-react';
import { logError, logInfo } from '../../../../utils/logger';

export const DatabaseSchemaGenerator = () => {
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentCollection, setCurrentCollection] = useState('');
  const [totalCollections, setTotalCollections] = useState(0);
  const [permissionErrors, setPermissionErrors] = useState([]);
  const [schema, setSchema] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleProgress = (event) => {
      setProgress(event.detail.progress);
      setCurrentCollection(event.detail.collection);
      setTotalCollections(event.detail.total);
    };

    const handleDocuments = (event) => {
      // Handle document count updates
    };

    const handleError = (event) => {
      setError(event.detail.message);
    };

    window.addEventListener('schemaGeneratorProgress', handleProgress);
    window.addEventListener('schemaGeneratorDocuments', handleDocuments);
    window.addEventListener('schemaGeneratorError', handleError);
    window.addEventListener('schemaGeneratorPermissionError', handlePermissionError);
    
    return () => {
      window.removeEventListener('schemaGeneratorProgress', handleProgress);
      window.removeEventListener('schemaGeneratorDocuments', handleDocuments);
      window.removeEventListener('schemaGeneratorError', handleError);
      window.removeEventListener('schemaGeneratorPermissionError', handlePermissionError);
    };
  }, []);

  const handleError = (event) => {
    setError(event.detail.message);
  };

  const handlePermissionError = (event) => {
    setPermissionErrors(prev => [...prev, event.detail]);
  };

  const handleGenerateSchema = async () => {
    try {
      setGenerating(true);
      setProgress(0);
      setCurrentCollection('');
      setTotalCollections(0);
      setPermissionErrors([]);
      setSchema(null);
      setError(null);
      
      const result = await generateDatabaseSchema();
      setSchema(result);
      logInfo('Schema generated successfully');
    } catch (err) {
      setError(err.message);
      logError('Error generating schema:', err);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">Database Schema Generator</h2>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleGenerateSchema}
            disabled={generating}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {generating ? 'Generating...' : 'Generate Schema'}
          </Button>
        </div>
      </div>

      {generating && (
        <div className="bg-gray-700/50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-4">
            <Database className="w-5 h-5 text-blue-400 animate-pulse" />
            <span className="text-gray-300">
              Scanning collection: {currentCollection}
            </span>
          </div>
          <div className="w-full bg-gray-600 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {permissionErrors.length > 0 && (
        <div className="bg-yellow-500/10 text-yellow-400 p-4 rounded-lg mb-4">
          <div className="flex items-start gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium">Permission Restrictions</h3>
              <p className="text-sm">
                Some collections couldn't be accessed due to Firebase security rules. This is normal and expected.
              </p>
            </div>
          </div>
          <div className="mt-2 text-sm">
            <p className="mb-1">Collections with permission restrictions: {permissionErrors.length}</p>
            <details>
              <summary className="cursor-pointer hover:text-yellow-300">View restricted collections</summary>
              <ul className="mt-2 ml-4 space-y-1 max-h-40 overflow-y-auto">
                {permissionErrors.map((error, index) => (
                  <li key={index}>{error.collection}</li>
                ))}
              </ul>
            </details>
          </div>
        </div>
      )}

      <div className="bg-blue-500/10 text-blue-400 p-4 rounded-lg mb-4">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium">About Firebase Security Rules</h3>
            <p className="text-sm mt-1">
              Firebase security rules prevent client-side access to collections you don't have permission for, even for admin users.
              This is a security feature, not a bug. To access all collections without restrictions, you would need to use the
              Firebase Admin SDK in a secure backend environment.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      {schema && (
        <div className="space-y-4">
          <div className="bg-gray-700/50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-white mb-2">Schema Generated Successfully</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Collections:</span>
                <span className="text-white ml-2">{Object.keys(schema.collections).length}</span>
              </div>
              <div>
                <span className="text-gray-400">Total Documents:</span>
                <span className="text-white ml-2">{schema.metadata.totalDocuments}</span>
              </div>
            </div>
          </div>
            
          <div className="bg-gray-700/50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-white mb-2">Collections Processed</h3>
            <div className="text-sm text-gray-400 mb-2">
              The following collections were successfully processed:
            </div>
            <div className="max-h-40 overflow-y-auto">
              <ul className="list-disc list-inside space-y-1">
                {Object.keys(schema.collections).map((collection) => (
                  <li key={collection} className="text-white">
                    {collection} <span className="text-gray-400">({schema.collections[collection].documentCount} documents)</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              onClick={() => exportSchemaToJSON(schema)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Export as JSON
            </Button>
            <Button
              onClick={() => exportSchemaAsHTML(schema)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Export as HTML
            </Button>
            <Button
              onClick={handleGenerateSchema}
              className="bg-gray-700 hover:bg-gray-600 text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Regenerate Schema
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseSchemaGenerator;